import { CounselingTypeEnum } from 'app/Bookings/constants'
import { callRudderEventForBookingCTAclicked } from 'app/ProviderSearch/rudderEventUtils'
import {
  getFilterIdsByValue,
  getSlotsStartEndDate,
} from 'app/ProviderSearch/utils'
import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import Button from 'component_library/1_Atoms/Button'
import {
  googleMapLibraries,
  INTAKE_BOOKING_WINDOW_HOURS,
} from 'constants/common'
import { usedForFeature } from 'constants/logEvents'
import { pageRoutes } from 'constants/routing'
import { RudderEvent } from 'constants/rudderEvents'
import {
  getAllProviderDetails,
  ProviderFilterTypes,
} from 'dataSource/ProviderSearch'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import withGoogleMapsScript from 'hoc/withGoogleMapsScript'
import useBreakpoint from 'hooks/useBreakPoint'
import useFetchLocation from 'hooks/useFetchLocation'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { getCurrentZonnedTime } from 'utils/common'
import logEvent from 'utils/logEvent'
import {
  providerTypes,
  SeFriendlyBookingButtonLocation,
  seFriendlyPhoneNumber,
} from '../constants'
import { stateAbbreviations } from '../validateUsaState'
import './index.scss'

type HubProviderCardProps = {
  type: string
  groupedFilters?: GroupedProviderFilter
}

const HubProviderCard: React.FC<HubProviderCardProps> = ({
  type,
  groupedFilters,
}) => {
  const router = useRouter()
  // getting user location based on user's ip address
  const { locationData } = useFetchLocation({
    usedForFeature: usedForFeature.SEFriendlyHubPageAutoSelectCurrentLocation,
  })
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  const isMobileOrTabScreen = isMobileScreen || isLargeSize

  const { phoneNumber, formattedPhoneNumber } = seFriendlyPhoneNumber
  const isPsychiatristsPage = type === providerTypes.PSYCHIATRISTS

  const [allProviders, setAllProviders] = useState([])
  const bookingWindowHours = INTAKE_BOOKING_WINDOW_HOURS

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const state = locationData?.state

  let counselingTypesValues = []

  if (type === providerTypes.THERAPISTS) {
    counselingTypesValues = [
      CounselingTypeEnum.INDIVIDUAL,
      CounselingTypeEnum.COUPLES,
      CounselingTypeEnum.FAMILY,
    ]
  } else if (type === providerTypes.PSYCHIATRISTS) {
    counselingTypesValues = [CounselingTypeEnum.MEDICATION]
  }

  const counselingTypeIds = getFilterIdsByValue(
    groupedFilters,
    ProviderFilterTypes.COUNSELING_TYPE,
    counselingTypesValues
  )

  const handleSeFriendlyPhoneCall = () => {
    window.location.href = `tel:${phoneNumber}`

    logEvent(RudderEvent.PHONE_NUMBER_CLICKED, {
      phone_number: formattedPhoneNumber,
    })
  }

  const onFindProviderBtnClick = (event) => {
    router.push(pageRoutes.PROVIDER_SEARCH)
    callRudderEventForBookingCTAclicked(
      SeFriendlyBookingButtonLocation.SE_FRIENDLY_INTERSTITIAL_SECTION,
      (event.currentTarget as HTMLElement).className,
      (event.currentTarget as HTMLElement).innerText
    )
  }

  // Fetches providers for the detected state using the current booking window.
  // isCancelled prevents a slower in-flight request (e.g. from a previous bookingWindowHours value)
  // from overwriting state after deps change or the component unmounts.
  useEffect(() => {
    if (!state) {
      return
    }

    let isCancelled = false

    const fetchAllProviderForState = async () => {
      const startSlotDate = getCurrentZonnedTime(timezone)
      const { startDate, endDate } = getSlotsStartEndDate(
        startSlotDate,
        5,
        timezone,
        undefined,
        bookingWindowHours
      )

      try {
        const responseProviderList = await getAllProviderDetails({
          location: `${state}, USA`,
          state: state,
          counselingType: counselingTypeIds,
          requestSource: 'se_friendly',
          slotStartDate: startDate.toISOString(),
          slotEndDate: endDate.toISOString(),
        })

        if (!isCancelled) {
          setAllProviders(responseProviderList.result)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch providers for state', error)

        if (!isCancelled) {
          setAllProviders([])
        }
      }
    }

    void fetchAllProviderForState()

    return () => {
      isCancelled = true
    }
  }, [state, bookingWindowHours])

  if (!state || allProviders.length === 0) {
    return null
  }

  return (
    <div className="HubProviderCard">
      <div className="HubProviderCard__header">
        <h1 className="HubProviderCard__header__title">
          Providers available near you
        </h1>
        <span className="HubProviderCard__header__subTitle">
          There are {allProviders.length} {isPsychiatristsPage && 'psychiatric'}{' '}
          providers in {stateAbbreviations[state]} available for{' '}
          {isPsychiatristsPage ? 'an online session.' : 'a therapy session.'}
        </span>
      </div>

      <div className="HubProviderCard__providerImageContainer">
        {allProviders?.slice(0, 4)?.map((provider) => (
          <Link
            key={provider.id}
            href={getProviderProfileRoute(provider)}
            passHref
          >
            <a
              href=" "
              className="HubProviderCard__providerImageInnerContainer"
            >
              <Image
                unoptimized
                key={provider.id}
                src={
                  provider?.profileImagePath?.length
                    ? provider.profileImagePath
                    : DEFAULT_PROFILE_IMAGE_PATH
                }
                alt="Provider profile image"
                width={isMobileOrTabScreen ? 76 : 88}
                height={isMobileOrTabScreen ? 76 : 88}
                className="HubProviderCard__providerImage"
                objectFit="cover"
              />
            </a>
          </Link>
        ))}
      </div>

      <div className="HubProviderCard__ctas">
        <Button
          variant="primary"
          theme="v2"
          // below classnames are used by data team
          className={`se-friendly-${
            isPsychiatristsPage ? 'psych' : 'therapist'
          }-cta-book-btn sticky-module-provider-section`}
          onClick={onFindProviderBtnClick}
        >
          Find a provider
        </Button>
        <Button
          variant="quinary"
          theme="v2"
          // below classnames are used by data team
          className={`se-friendly-${
            isPsychiatristsPage ? 'psych' : 'therapist'
          }-cta-call-btn sticky-module-provider-section`}
          onClick={handleSeFriendlyPhoneCall}
        >
          Call {formattedPhoneNumber}
        </Button>
      </div>
    </div>
  )
}

export default withGoogleMapsScript(HubProviderCard, [
  googleMapLibraries.PLACES,
])
